import React, { FC } from "react"
import { graphql } from "gatsby"
import type { PageProps } from "gatsby"

import type { PostsLandingQueryQuery, PostCategoryFragmentFragment, CardFragmentFragment } from "@graphqlTypes"

import BespokeComponent from "@components/lp/BespokeComponent/BespokeComponent"
import CTA from "@components/CTA/CTA"
import Cards, { CARDS_VARIANTS } from "@components/Cards/Cards"
import GetStartedPrefooter from "@components/prefooters/GetStartedPrefooter/GetStartedPrefooter"
import SEO from "@components/SEO/SEO"

import PostsHero from "../components/PostsHero/PostsHero"
import PostsLandingSubscribeCallout from "../components/PostsLandingSubscribeCallout/PostsLandingSubscribeCallout"

import {
  PostsLandingCTASECTION,
  PostsLandingEditorsPicksSECTION,
  PostsLandingFurtherReadingSECTION,
  PostsLandingMostRecentSECTION,
} from "./PostsLanding.styles"

export const Head: FC<PageProps<PostsLandingQueryQuery>> = ({
  data: {
    category,
  }
}) => (
  <SEO
    description={category?.seoDescription || ""}
    image={category?.seoImage?.imgixImage?.url || ""}
    permalink={category?.permalink || ""}
    title={category?.seoTitle || ""}
  />
)

const PostsLanding: FC<PageProps<PostsLandingQueryQuery>> = ({
  data: {
    mostRecentPosts,
    category,
    allCategoriesData: { nodes: allCategories },
  }
}) => {
  return (
    <>
      <PostsHero
        allCategories={allCategories}
        categoryPermalink={category?.permalink}
        categorySlug={category?.slug}
        description={category?.seoDescription}
        isLandingPage={category?.slug === "all"}
        title={category?.title}
        topics={category?.subcategories}
      />

      {mostRecentPosts && mostRecentPosts.length > 0 && (
        <PostsLandingMostRecentSECTION>
          <div className="container">
            <Cards
              isAboveFold
              items={mostRecentPosts}
              title="Most Recent Posts"
              moreLink={`${category?.permalink}all-posts/`}
              moreText="View All Posts"
              showSubcategory={category?.slug !== "all"}
              variant={CARDS_VARIANTS.WITH_FEATURED}
            />
          </div>
        </PostsLandingMostRecentSECTION>
      )}

      {/* First CTA */}
      {category && category.cta && (
        <>
          {category.cta.__typename === "ContentfulCta" && (
            <PostsLandingCTASECTION>
              <div className="container">
                <CTA {...category.cta} />
              </div>
            </PostsLandingCTASECTION>
          )}
          {["ContentfulPost", "ContentfulLandingPage"].includes(category.cta.__typename) && (
            <PostsLandingCTASECTION>
              <div className="container">
                <Cards
                  key={`resources-card-post-${category.cta.id}`}
                  items={[category.cta]}
                  showSubcategory={category.slug !== "all"}
                  variant={CARDS_VARIANTS.WITH_FEATURED}
                />
              </div>
            </PostsLandingCTASECTION>
          )}
          {category.cta.__typename === "ContentfulLpBespokeComponent" && (
            <BespokeComponent contentful_id="" {...category.cta} />
          )}
        </>
      )}

      {/* Discover the latest in... */}
      {category && category.featuredSubcategories && (
        <PostsLandingFurtherReadingSECTION>
          <div className="container">
            <h2>Discover the latest in...</h2>
            {category.featuredSubcategories.map((featuredSubcategory) => {
              if (
                !featuredSubcategory ||
                !featuredSubcategory.recentPostsAndLandingPages ||
                featuredSubcategory.recentPostsAndLandingPages?.length === 0
              )
                return null

              return (
                <Cards
                  items={featuredSubcategory.recentPostsAndLandingPages as CardFragmentFragment[]}
                  key={featuredSubcategory.slug}
                  moreLink={featuredSubcategory.permalink || undefined}
                  moreText="View More Posts"
                  showSubcategory
                  title={featuredSubcategory.title || undefined}
                />
              )
            })}
          </div>
        </PostsLandingFurtherReadingSECTION>
      )}

      {/* Second CTA */}
      {category && category.ctaSecond && (
        <PostsLandingCTASECTION>
          <div className="container">
            {category.ctaSecond.__typename === "ContentfulCta" && (
              <CTA {...category.ctaSecond} />
            )}
          </div>
        </PostsLandingCTASECTION>
      )}

      {/* Editor's Picks */}
      {category &&
        category.slug !== "all" &&
        category.featuredPosts && (
          <PostsLandingEditorsPicksSECTION>
            <div className="container">
              <div className="grid">
                <div className="column column--1-8-L column--1-9-XL">
                  <Cards
                    items={category.featuredPosts as CardFragmentFragment[]}
                    title="Editor’s Picks"
                    showSubcategory={category.slug !== "all"}
                    variant={CARDS_VARIANTS.COMPACT}
                  />
                </div>
                <div className="column column--3-11-M column--8-13-L column--9-13-XL">
                  <PostsLandingSubscribeCallout />
                </div>
              </div>
            </div>
          </PostsLandingEditorsPicksSECTION>
        )}

      <GetStartedPrefooter />
    </>
  )
}

export const pageQuery = graphql`
  fragment PostCategoryFragment on ContentfulPostCategory {
    id
    permalink
    slug
    title
  }

  fragment PostSubCategoryFragment on ContentfulPostSubCategory {
    id
    permalink
    slug
    title
  }

  fragment PostCategoryFullDataFragment on ContentfulPostCategory {
    id
    cta {
      __typename
      ... on ContentfulCta {
        ...CtaFragment
      }
      ... on ContentfulPost {
        ...CardFragment
      }
      # ... on ContentfulLandingPage {
      #   ...CardLpFragment
      # }
      ... on ContentfulLpBespokeComponent {
        componentId
      }
    }
    ctaSecond {
      __typename
      ... on ContentfulCta {
        ...CtaFragment
      }
    }
    featuredPosts {
      ...CardPostOrLpFragment
    }
    featuredSubcategories {
      ... on ContentfulPostCategory {
        ...PostCategoryFragment
        recentPostsAndLandingPages {
          ...CardPostOrLpFragment
        }
      }
      ... on ContentfulPostSubCategory {
        ...PostSubCategoryFragment
        recentPostsAndLandingPages {
          ...CardPostOrLpFragment
        }
      }
    }
    permalink
    subcategories {
      ... on ContentfulPostSubCategory {
        ...PostSubCategoryFragment
      }
    }
    seoDescription
    seoImage {
      imgixImage {
        url
      }
    }
    seoTitle
    slug
    title
  }

  query PostsLandingQuery(
    $categoryId: String!
  ) {
    category: contentfulPostCategory(contentful_id: { eq: $categoryId }) {
      ...PostCategoryFullDataFragment
    }
    mostRecentPosts: allPostsAndLandingPages(
      limit: 3
      categoryId: $categoryId
    ) {
      ...CardPostOrLpFragment
    }
    allCategoriesData: allContentfulPostCategory(
      sort: {navPosition: ASC}
    ) {
      nodes {
        ...PostCategoryFragment
      }
    }
  }
`

export default PostsLanding
